/**
 * Fumee scroll reveal.
 *
 * Most homepage sections already carried an entrance animation, but nothing
 * gated it: every animation started on page load, so a section four screens down
 * had finished long before anyone scrolled to it and was sitting there settled
 * by the time it came into view. The stock sections (hero, marquee, the product
 * grid, the FAQ) had no entrance at all.
 *
 * This marks each section, holds it, and releases it when it arrives on screen.
 *
 * The held state is added from JavaScript on purpose. If this file fails to load
 * or IntersectionObserver is missing, nothing is ever hidden and the page reads
 * exactly as it did before.
 */

(() => {
  const REVEAL_ATTR = 'data-fumee-reveal';
  const REVEAL_CLASS = 'is-revealed';

  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // How far into the viewport a section must come before it is released, so the
  // movement happens where it can be seen rather than finishing off screen.
  const MARGIN = 0.12;

  /** @type {Set<Element>} */
  const held = new Set();

  /**
   * @param {Element} section
   */
  const reveal = (section) => {
    held.delete(section);
    section.classList.add(REVEAL_CLASS);
    observer.unobserve(section);
  };

  /**
   * @param {Element} section
   */
  const isOnScreen = (section) => {
    const rect = section.getBoundingClientRect();
    return rect.top < window.innerHeight * (1 - MARGIN) && rect.bottom > 0;
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    { rootMargin: `0px 0px -${MARGIN * 100}% 0px`, threshold: 0.01 }
  );

  // Belt and braces. This script hides every section up front, so a single point
  // of failure would leave the page blank below the fold. Scroll events are
  // delivered independently of the observer, so they are checked too: whichever
  // fires first releases the section, and the other finds nothing left to do.
  let lastSweep = 0;
  const sweep = () => {
    const now = Date.now();
    if (now - lastSweep < 120) return;
    lastSweep = now;

    for (const section of held) {
      if (isOnScreen(section)) reveal(section);
    }

    if (!held.size) {
      window.removeEventListener('scroll', sweep);
      window.removeEventListener('resize', sweep);
    }
  };

  window.addEventListener('scroll', sweep, { passive: true });
  window.addEventListener('resize', sweep, { passive: true });

  /**
   * @param {Element} section
   */
  const watch = (section) => {
    if (section.hasAttribute(REVEAL_ATTR)) return;
    section.setAttribute(REVEAL_ATTR, '');

    // Anything already on screen when the page loads — the hero, usually —
    // is released in the same tick, so it never waits on a scroll that may not
    // come and never paints in its held state.
    if (isOnScreen(section)) {
      section.classList.add(REVEAL_CLASS);
      return;
    }

    held.add(section);
    observer.observe(section);
  };

  const scan = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;

    for (const section of main.querySelectorAll('.shopify-section')) watch(section);
  };

  scan();

  // Images and fonts landing late move sections around, so a second pass once
  // everything has settled catches anything that ended up on screen after all.
  window.addEventListener(
    'load',
    () => {
      scan();
      sweep();
    },
    { once: true }
  );

  // The theme editor replaces a section wholesale when its settings change.
  document.addEventListener('shopify:section:load', scan);
})();
